import { createContext, ReactNode, useContext, useState, useRef, useCallback } from 'react';
import { WebSocketContext } from './websocket-context/websocket-provider';
import type { ModelParameters } from '../pages/pricing/sections/pricing-model';
import usePersistentState from './use-persistent-state';

interface IParamsProvider {
  children: ReactNode;
}

export interface GenericPricerParams {
  tradeId: string;
  underlying: string;
  accounting: string;
  contract:
    | 'OneTouch (Upper)'
    | 'OneTouch (Lower)'
    | 'DoubleNoTouch'
    | 'BarrierOption (Upper)'
    | 'BarrierOption (Lower)'
    | 'EuropeanOption'
    | 'AmericanOption'
    | '';
  expiry: string;
  notionalToken: string;
  notionalAmount: number;
  barrierUpper?: number;
  barrierLower?: number;
  paymentTime?: 'at_touch' | 'at_expiry' | '';
  strike?: number;
  callPut?: 'CALL' | 'PUT' | '';
}

interface TradeHandler {
  submit: () => void;
  validate: () => Promise<boolean>;
  getFormData: () => any;
}

interface IProvider {
  trades: TradeItem[];
  setTrades: any;
  fetchGenericPricer: (params: GenericPricerParams) => void;
  getTradeResult: (tradeId: string) => any;
  isAnyTradeLoading: boolean;
  getTradeLoading: (tradeId: string) => boolean;
  getTradeError: (tradeId: string) => boolean;
  getTradeErrorMessage: (tradeId: string) => string;
  registerTradeHandler: (id: string, handler: TradeHandler) => void;
  unregisterTradeHandler: (id: string) => void;
  triggerAllSubmits: () => void;
  getTradeModel: (tradeId: string) => string;
  setTradeModel: (tradeId: string, model: string) => void;
  getTradeModelParams: (tradeId: string) => ModelParameters;
  deleteTrade: (tradeId: string) => void;
  duplicateTrade: (tradeId: string, formData: any) => string;
  tradeFormData: Record<string, any>;
  setTradeFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  showRiskLadder: boolean;
  setShowRiskLadder: React.Dispatch<React.SetStateAction<boolean>>;
}

type TradeItem = {
  id: string;
  data: any;
};

export const PricingContext = createContext<IProvider>({} as IProvider);

const DEFAULT_MODEL_PARAMS: ModelParameters = {
  num_x_points: 120,
  num_y_points: 50,
  num_t_points: 80,
  num_x_std_devs: 7,
  num_y_std_devs: 5,
};

const STATE_PATH = ['PRICING'];

export const PricingProvider = (props: IParamsProvider) => {
  const [trades, setTrades] = usePersistentState<TradeItem[]>(STATE_PATH, 'trades', [
    { id: 'trade_1', data: {} },
  ]);
  const [loadingByTrade, setLoadingByTrade] = useState<Record<string, boolean>>({});
  const [errorByTrade, setErrorByTrade] = useState<
    Record<string, { hasError: boolean; message?: string }>
  >({});
  const [pricingModels, setPricingModels] = useState<Record<string, string>>({});
  const [modelParameters] = useState<Record<string, ModelParameters>>({});
  const [tradeFormData, setTradeFormData] = usePersistentState<Record<string, any>>(
    STATE_PATH,
    'tradeFormData',
    { trade_1: {} },
  );
  const [showRiskLadder, setShowRiskLadder] = useState(false);

  const { sendMessage, subscribeToChannel } = useContext(WebSocketContext);
  const tradeHandlersRef = useRef<Map<string, TradeHandler>>(new Map());
  const tradeIdMapRef = useRef<Map<string, string>>(new Map());

  const GENERIC_PRICER_ID_KEY = 'GENERIC_PRICER_ID';

  const registerTradeHandler = useCallback((id: string, handler: TradeHandler) => {
    tradeHandlersRef.current.set(id, handler);
  }, []);

  const unregisterTradeHandler = useCallback((id: string) => {
    tradeHandlersRef.current.delete(id);
  }, []);

  const triggerAllSubmits = useCallback(async () => {
    const handlers = Array.from(tradeHandlersRef.current.values());
    const validations = await Promise.all(handlers.map((h) => h.validate()));
    const allValid = validations.every((isValid) => isValid);
    if (allValid) {
      handlers.forEach((h) => {
        h.submit();
      });
    }
  }, []);

  const isAnyTradeLoading = Object.values(loadingByTrade).some((loading) => loading);

  const getTradeLoading = useCallback(
    (tradeId: string) => loadingByTrade[tradeId] || false,
    [loadingByTrade],
  );

  const getTradeError = useCallback(
    (tradeId: string) => errorByTrade[tradeId]?.hasError || false,
    [errorByTrade],
  );

  const getTradeErrorMessage = useCallback(
    (tradeId: string) => errorByTrade[tradeId]?.message || '',
    [errorByTrade],
  );

  const getErrorMessage = (error: any) => {
    if (!error) return '';
    if (typeof error === 'string') return error;
    if (typeof error?.message === 'string') return error.message;
    return '';
  };

  const handleGenericPricerResponse = (message: any) => {
    let payload = message;
    if (typeof message === 'string') {
      try {
        payload = JSON.parse(message);
      } catch {
        return;
      }
    }

    if (!payload || !payload.id || !payload.id.startsWith(GENERIC_PRICER_ID_KEY)) return;

    const tradeId = tradeIdMapRef.current.get(payload.id);
    if (!tradeId) return;

    if (payload?.error) {
      const errorMessage = getErrorMessage(payload?.error);
      setErrorByTrade((prev) => ({
        ...prev,
        [tradeId]: { hasError: true, message: errorMessage },
      }));
      setTrades((prev) =>
        prev.map((trade) => (trade.id === tradeId ? { ...trade, data: {} } : trade)),
      );
      setLoadingByTrade((prev) => ({ ...prev, [tradeId]: false }));
      return;
    }

    setTrades((prev) =>
      prev.map((trade) =>
        trade.id === tradeId ? { ...trade, data: payload.result || payload } : trade,
      ),
    );
    setErrorByTrade((prev) => ({
      ...prev,
      [tradeId]: { hasError: false, message: '' },
    }));
    setLoadingByTrade((prev) => ({ ...prev, [tradeId]: false }));
  };

  const getTradeResult = useCallback(
    (tradeId: string) => {
      const trade = trades.find((t) => t.id === tradeId);
      return trade?.data || null;
    },
    [trades],
  );

  const getTradeModel = useCallback(
    (tradeId: string) => {
      return pricingModels[tradeId] || 'BS';
    },
    [pricingModels],
  );

  const setTradeModel = useCallback((tradeId: string, model: string) => {
    setPricingModels((prev: any) => ({
      ...prev,
      [tradeId]: model,
    }));
  }, []);

  const getTradeModelParams = useCallback(
    (tradeId: string) => {
      return modelParameters[tradeId] || DEFAULT_MODEL_PARAMS;
    },
    [modelParameters],
  );

  const deleteTrade = useCallback(
    (tradeId: string) => {
      setTrades((prev: TradeItem[]) => prev.filter((trade) => trade.id !== tradeId));
      unregisterTradeHandler(tradeId);
      setLoadingByTrade((prev) => {
        const newLoading = { ...prev };
        delete newLoading[tradeId];
        return newLoading;
      });
      setErrorByTrade((prev) => {
        const newError = { ...prev };
        delete newError[tradeId];
        return newError;
      });
      setPricingModels((prev) => {
        const newModels = { ...prev };
        delete newModels[tradeId];
        return newModels;
      });
      setTradeFormData((prev) => {
        const newFormData = { ...prev };
        delete newFormData[tradeId];
        return newFormData;
      });
    },
    [unregisterTradeHandler],
  );

  const duplicateTrade = useCallback(
    (tradeId: string, formData: any) => {
      let newId = '';
      setTrades((prev: TradeItem[]) => {
        const existingIds = new Set(prev.map((trade: TradeItem) => trade.id));
        let nextNumber = prev.length + 1;
        let nextId = `trade_${nextNumber}`;
        while (existingIds.has(nextId)) {
          nextNumber++;
          nextId = `trade_${nextNumber}`;
        }
        newId = nextId;

        setTradeFormData((prevData) => ({
          ...prevData,
          [nextId]: formData || {},
        }));
        const originalModel = pricingModels[tradeId] || 'BS';
        setPricingModels((prevModels) => ({
          ...prevModels,
          [nextId]: originalModel,
        }));
        const originalIndex = prev.findIndex((trade) => trade.id === tradeId);
        const newTrade = { id: nextId, data: {} };
        const newTrades = [...prev];
        newTrades.splice(originalIndex + 1, 0, newTrade);
        return newTrades;
      });
      return newId;
    },
    [pricingModels],
  );

  const fetchGenericPricer = (params: GenericPricerParams) => {
    setErrorByTrade((prev) => ({
      ...prev,
      [params.tradeId]: { hasError: false, message: '' },
    }));
    setLoadingByTrade((prev) => ({ ...prev, [params.tradeId]: true }));

    const requestId = `${GENERIC_PRICER_ID_KEY}_${params.tradeId}`;
    const tradeModel = pricingModels[params.tradeId] || 'BS';

    tradeIdMapRef.current.set(requestId, params.tradeId);

    let instrumentName = '';
    let payoff: any = {};

    switch (params.contract) {
      case 'OneTouch (Upper)':
      case 'OneTouch (Lower)': {
        const isUpper = params.contract === 'OneTouch (Upper)';
        const barrier = isUpper ? params.barrierUpper : params.barrierLower;
        instrumentName = 'one_touch';
        payoff = {
          barrier: barrier,
          barrier_direction: isUpper ? 'upper' : 'lower',
          pay_type: params.paymentTime,
          payout: 1.0,
          payout_token: params.notionalToken,
        };
        break;
      }
      case 'DoubleNoTouch': {
        instrumentName = 'double_no_touch';
        payoff = {
          upper_barrier: params.barrierUpper,
          lower_barrier: params.barrierLower,
          payout: 1.0,
          payout_token: params.notionalToken,
        };
        break;
      }
      case 'BarrierOption (Upper)':
      case 'BarrierOption (Lower)': {
        const isUpper = params.contract === 'BarrierOption (Upper)';
        instrumentName = 'barrier_option';
        payoff = {
          strike: params.strike,
          cp: params.callPut || '',
          barrier: isUpper ? params.barrierUpper : params.barrierLower,
          barrier_direction: isUpper ? 'upper' : 'lower',
        };
        break;
      }
      case 'EuropeanOption': {
        instrumentName = 'european_option';
        payoff = {
          strike: params.strike,
          cp: params.callPut || '',
        };
        break;
      }
      case 'AmericanOption': {
        instrumentName = 'american_option';
        payoff = {
          strike: params.strike,
          cp: params.callPut || '',
        };
        break;
      }
    }

    const req = {
      jsonrpc: '2.0',
      id: requestId,
      method: 'generic_pricer',
      params: {
        request: ['PV', 'Delta', 'Vega'],
        position: {
          instrument: {
            type: instrumentName,
            und_token: params.underlying,
            acc_token: params.accounting,
            expiry: `${params.expiry} 08:00:00`,
            payoff: payoff,
          },
          notional: params.notionalAmount,
        },
        pricing_parameters: {
          model: tradeModel,
        },
        risk_ladder: showRiskLadder,
      },
    };

    sendMessage(JSON.stringify(req));
    subscribeToChannel(requestId, handleGenericPricerResponse);
  };

  return (
    <PricingContext.Provider
      value={{
        trades,
        setTrades,
        fetchGenericPricer,
        getTradeResult,
        isAnyTradeLoading,
        getTradeLoading,
        getTradeError,
        getTradeErrorMessage,
        registerTradeHandler,
        unregisterTradeHandler,
        triggerAllSubmits,
        getTradeModel,
        setTradeModel,
        getTradeModelParams,
        deleteTrade,
        duplicateTrade,
        tradeFormData,
        setTradeFormData,
        showRiskLadder,
        setShowRiskLadder,
      }}
    >
      {props.children}
    </PricingContext.Provider>
  );
};
